import React, { useEffect, useRef, useState } from "react";

/**
 * MarketingPage
 * -------------
 * Renders one of the redesign-brief HTML pages EXACTLY as exported.
 *
 * How it works:
 *  1. Fetch the raw HTML from /marketing/<file>
 *  2. Parse it to extract the <body> content, any inline <style> tags from
 *     <head>, and any inline / external <script> elements.
 *  3. Inject the styles into <head>.
 *  4. Set the body HTML on a container via innerHTML.
 *  5. Re-create <script> tags so the browser actually executes them
 *     (React's dangerouslySetInnerHTML never runs injected scripts).
 *
 * This preserves pixel-perfect fidelity with the design brief because
 * no markup or CSS is rewritten by hand.
 */
const MarketingPage = ({ file }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const injectedNodes = [];

    (async () => {
      try {
        const res = await fetch(`/marketing/${file}`);
        if (!res.ok) throw new Error(`Failed to load ${file}: ${res.status}`);
        const html = await res.text();

        if (cancelled) return;
        const doc = new DOMParser().parseFromString(html, "text/html");

        // 1. Set <html data-theme="…"> so the redesign tokens & theme.js sync
        const themeAttr = doc.documentElement.getAttribute("data-theme");
        if (themeAttr) document.documentElement.setAttribute("data-theme", themeAttr);
        document.title = doc.title || document.title;

        // 2. Move all <link rel="stylesheet"> and <style> from doc <head>
        //    into the live document head so the page picks them up.
        const headTargets = doc.head.querySelectorAll(
          "link[rel='stylesheet'], link[rel='preconnect'], style"
        );
        headTargets.forEach((node) => {
          const clone = node.cloneNode(true);
          clone.setAttribute("data-marketing-injected", "true");
          document.head.appendChild(clone);
          injectedNodes.push(clone);
        });

        // 3. Body content via innerHTML (fast, faithful)
        if (containerRef.current) {
          containerRef.current.innerHTML = doc.body.innerHTML;

          // 4. Re-create scripts inside body so they execute
          const scripts = containerRef.current.querySelectorAll("script");
          scripts.forEach((oldScript) => {
            const s = document.createElement("script");
            for (const attr of oldScript.attributes) {
              s.setAttribute(attr.name, attr.value);
            }
            s.text = oldScript.textContent;
            oldScript.replaceWith(s);
          });
        }

        // 5. Also execute head-level scripts (theme.js, scroll-motion.js)
        doc.head.querySelectorAll("script").forEach((oldScript) => {
          const s = document.createElement("script");
          for (const attr of oldScript.attributes) {
            s.setAttribute(attr.name, attr.value);
          }
          s.text = oldScript.textContent;
          s.setAttribute("data-marketing-injected", "true");
          document.head.appendChild(s);
          injectedNodes.push(s);
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[MarketingPage] load failed:", err);
        if (!cancelled) setError(err.message);
      }
    })();

    return () => {
      cancelled = true;
      injectedNodes.forEach((n) => n.remove());
    };
  }, [file]);

  if (error) {
    return (
      <div style={{ padding: 40, fontFamily: "sans-serif" }}>
        <h1>Couldn't load marketing page</h1>
        <p>{error}</p>
      </div>
    );
  }

  return <div ref={containerRef} data-testid={`marketing-${file}`} />;
};

export default MarketingPage;
